import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BallotForm } from '@asd-stan/ballot/domain/ballot.entity';
import { calculateOffset } from '@asd-stan/helpers/app-utils';
import { getDomainService } from '@asd-stan/domain/infrastructure/getters';
import { UserSelectSearch } from '@asd-stan/standard/components/single-standard/stage-update/additional/user-select-search';
import {
	getStandardService,
} from '@asd-stan/standard/infrastructure/getters';
import { InputGroup } from '@asd-stan/user/components/layout/input-group/input-group';
import { FormDatePicker } from '@components/form-date-picker/form-date-picker';
import { FormInputParagraph } from '@components/form-input-paragraph/form-input-paragraph';
import { FormInput } from '@components/form-input/form-input';
import { FormSelect } from '@components/form-select/form-select';
import {
	AdditionalType,
	FormSelectAsyncPagination,
} from '@components/form-select/form-select-async-pagination';
import { Field, getIn, useFormikContext } from 'formik';
import moment from 'moment';

const limit = 6;

export const General = observer(() => {
	const { t } = useTranslation();
	const standardService = getStandardService();
	const { values, setFieldValue, errors, touched, submitCount, setFieldTouched } =
		useFormikContext<BallotForm>();

	const domainService = getDomainService();

	const domainOptions = domainService.domains.map(domain => {
		return { label: `${domain.code} ${domain.name}`, value: domain.code };
	});

	const wgOptions = values.domains
		.flatMap(({ value }) => domainService.getDomainWorkingGroups(value))
		.map(({ code, name }) => ({ label: `${code} ${name}`, value: code }));

	const handleLoadLatestStandards = async (
		search: string,
		_prevOptions: any,
		{ page }: AdditionalType
	) => {
		const offset = calculateOffset(page, limit);
		const count = await standardService.getLatestStandardsList(limit, offset, {
			registrationNumber: search,
			isDraft: false,
		});
		const updateStandards = standardService.latestStandards.map(standard => {
			return {
				label: `${standard.registrationNumber ?? ''} ${standard.form ?? ''} ${
					standard.edition ?? ''
				} ${standard.localizedTitle}`,
				value: standard.id,
			};
		});
		const hasMore = page < Math.ceil(count / 6);
		return {
			options: updateStandards.filter(
				standard => standard.value !== standardService.singleStandard?.id
			),
			hasMore,
			additional: {
				page: page + 1,
			},
		};
	};

	return (
		<InputGroup title={t('ballot.createBallot.general')}>
			<Field
				component={FormSelect}
				isMulti
				options={domainOptions}
				name="domains"
				title={t('ballot.createBallot.domains')}
				customChange={() => {
					setFieldValue('workingGroups', []);
				}}
				showError
			/>
			<Field
				component={FormSelect}
				isMulti
				options={wgOptions}
				name="workingGroups"
				title={t('ballot.createBallot.workingGroups')}
				showError
				disabled={values.domains.length === 0}
			/>
			<Field
				component={FormInputParagraph}
				name="title"
				title={t('ballot.createBallot.titleField')}
				mandatory
				fullWidth
				showError
			/>
			<Field
				component={FormInputParagraph}
				name="description"
				title={t('ballot.createBallot.description')}
				fullWidth
				showError
				maxLength={5000}
			/>
			<Field
				component={FormSelectAsyncPagination}
				name="standard"
				title={t('ballot.createBallot.standard')}
				loadOptions={handleLoadLatestStandards}
				fullWidth
				showError
				placeholder={t('ballot.createBallot.standardPlaceholder')}
			/>
			<Field
				component={FormDatePicker}
				name="openingDate"
				label={t('ballot.createBallot.openingDate')}
				showError
				onChange={(_: string, date: Date) => {
					if (moment(values.closingDate).isBefore(date)) {
						setFieldValue('closingDate', null);
					}
				}}
				placeholder={t('ballot.createBallot.datePlaceholder')}
			/>
			<Field
				component={FormDatePicker}
				name="closingDate"
				label={t('ballot.createBallot.closingDate')}
				showError
				minDate={values.openingDate}
				placeholder={t('ballot.createBallot.datePlaceholder')}
			/>
			<UserSelectSearch
				fieldTitle={t('ballot.createBallot.voters')}
				value={values.selectedVoters}
				onChange={newValue => setFieldValue('selectedVoters', newValue)}
				onBlur={() => setFieldTouched('selectedVoters', true)}
				error={
					!!getIn(touched, 'selectedVoters') || submitCount > 0
						? (getIn(errors, 'selectedVoters') as string | undefined)
						: undefined
				}
			/>
		</InputGroup>
	);
});
